import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    type ChartConfig,
    NCoreUIKitTheme,
    ToggleGroup,
    RadialChart,
    AreaChart,
    Progress,
    Slider,
    Button,
    Switch,
    Badge,
    Chart,
    Card,
    Chip,
    Icon,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    SlidersHorizontal,
    BatteryCharging,
    TrendingDown,
    Lightbulb,
    Snowflake,
    Sparkles,
    CloudSun,
    Sunrise,
    LogOut,
    Blinds,
    Flame,
    Check,
    Minus,
    Play,
    Wind,
    Film,
    Lock,
    Plus,
    Bot
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const SlidersIcon = createKitIcon(SlidersHorizontal);

const ChargingIcon = createKitIcon(BatteryCharging);

const TrendingDownIcon = createKitIcon(TrendingDown);

const SunriseIcon = createKitIcon(Sunrise);

const SparklesIcon = createKitIcon(Sparkles);

const SnowflakeIcon = createKitIcon(Snowflake);

const LogOutIcon = createKitIcon(LogOut);

const FlameIcon = createKitIcon(Flame);

const CheckIcon = createKitIcon(Check);

const MinusIcon = createKitIcon(Minus);

const PlayIcon = createKitIcon(Play);

const FilmIcon = createKitIcon(Film);

const PlusIcon = createKitIcon(Plus);

const ROOMS = [
    {
        id: "living",
        count: 6
    },
    {
        id: "kitchen",
        count: 4
    },
    {
        id: "bedroom",
        count: 5
    },
    {
        id: "kids",
        count: 3
    }
];

const SCENES = [
    {
        icon: SunriseIcon,
        id: "morning"
    },
    {
        icon: FilmIcon,
        id: "movie"
    },
    {
        icon: LogOutIcon,
        id: "away"
    }
];

const MODES = [
    {
        icon: FlameIcon,
        id: "heat"
    },
    {
        icon: SnowflakeIcon,
        id: "cool"
    },
    {
        icon: SparklesIcon,
        id: "auto"
    }
];

const HOURLY_KWH = [
    0.3,
    0.3,
    0.3,
    0.3,
    0.3,
    0.4,
    0.9,
    1.3,
    1.1,
    0.8,
    0.5,
    0.5,
    0.7,
    0.9,
    0.6,
    0.6
];

const CURRENT_TEMPERATURE = 22.5;

const MIN_TEMPERATURE = 10;

const MAX_TEMPERATURE = 30;

const SLIDER_MIN = 16;

const SLIDER_MAX = 30;

const TEMPERATURE_STEP = 0.5;

const OUTSIDE_TEMPERATURE = 14;

const stylesheet = StyleSheet.create({
    tileTop: {
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexDirection: "row"
    },
    header: {
        justifyContent: "space-between",
        alignItems: "flex-end",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    energyFooter: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    target: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    titleRow: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    range: {
        justifyContent: "space-between",
        flexDirection: "row"
    },
    iconBox: {
        justifyContent: "center",
        alignItems: "center"
    },
    tile: {
        flexDirection: "column",
        flexGrow: 1
    },
    column: {
        flexDirection: "column"
    },
    start: {
        alignSelf: "flex-start"
    },
    scenes: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    weather: {
        alignItems: "center",
        flexDirection: "row"
    },
    badges: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    tiles: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    thermostat: {
        alignItems: "center"
    },
    screen: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    center: {
        alignItems: "center"
    },
    selfCenter: {
        alignSelf: "center"
    },
    gauge: {
        maxWidth: "100%",
        width: "100%"
    },
    grow: {
        minWidth: 0,
        flex: 1
    }
});

const handlePress = () => undefined;

const SmartHome01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        room,
        setRoom
    ] = useState("living");

    const [
        scene,
        setScene
    ] = useState<string | undefined>(undefined);

    const [
        target,
        setTarget
    ] = useState(23);

    const [
        mode,
        setMode
    ] = useState("heat");

    const [
        isLightOn,
        setIsLightOn
    ] = useState(true);

    const [
        brightness,
        setBrightness
    ] = useState(70);

    const [
        curtains,
        setCurtains
    ] = useState(60);

    const [
        isClimateOn,
        setIsClimateOn
    ] = useState(false);

    const [
        isCleaning,
        setIsCleaning
    ] = useState(false);

    const [
        isLocked,
        setIsLocked
    ] = useState(true);

    const isWide = width >= shapeConfigs.compactBreakpoint;

    const columnWidth = isWide ? "33.3333%" : "50%";

    const decimal = new Intl.NumberFormat(activeLocale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    });

    const integer = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0
    });

    const percent = new Intl.NumberFormat(activeLocale, {
        style: "percent"
    });

    const dateText = new Intl.DateTimeFormat(activeLocale, {
        weekday: "long",
        day: "numeric",
        month: "long"
    }).format(new Date(2026, 10, 2));

    const formatTemperature = (value: number) => `${decimal.format(value)} °C`;

    const formatKwh = (value: number) => t("blocks.smartHome01.kwh", [
        decimal.format(value)
    ]);

    const formatHour = (value: number | string) => `${String(value).padStart(2, "0")}:00`;

    const totalKwh = HOURLY_KWH.reduce((sum, value) => sum + value, 0);

    const energyConfig: ChartConfig = {
        kwh: {
            title: t("blocks.smartHome01.energyTitle"),
            colorIndex: 1
        }
    };

    const energyRows = HOURLY_KWH.map((kwh, hour) => ({
        hour: formatHour(hour),
        kwh
    }));

    const handleModeChange = (value: string | Array<string>) => {
        if(typeof value === "string") {
            setMode(value);
        }
    };

    const handleTarget = (step: number) => () => setTarget(Math.min(SLIDER_MAX, Math.max(SLIDER_MIN, target + step)));

    const handleTargetSlide = (value: number | Array<number>) => {
        if(typeof value === "number") {
            setTarget(value);
        }
    };

    const handleBrightness = (value: number | Array<number>) => {
        if(typeof value === "number") {
            setBrightness(Math.round(value));
        }
    };

    const handleCurtains = (value: number | Array<number>) => {
        if(typeof value === "number") {
            setCurtains(Math.round(value));
        }
    };

    const handleScene = (id: string) => () => setScene(scene === id ? undefined : id);

    const renderIconBox = (icon: typeof Lightbulb) => <View
        style={[
            stylesheet.iconBox,
            {
                backgroundColor: colors.content.container.selected,
                height: controlHeights.medium,
                width: controlHeights.medium,
                borderRadius: radiuses.md
            }
        ]}
    >
        <Icon
            color="mid"
            icon={icon}
        />
    </View>;

    const renderTile = (content: React.ReactNode, key: string) => <View
        style={{
            padding: spaces.spacingMd / 2,
            width: columnWidth
        }}
        key={key}
    >
        <Card
            style={stylesheet.tile}
            variant="outline"
        >
            <Card.Content>
                <View
                    style={[
                        stylesheet.column,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    {content}
                </View>
            </Card.Content>
        </Card>
    </View>;

    const renderTop = (icon: typeof Lightbulb, trailing: React.ReactNode) => <View
        style={stylesheet.tileTop}
    >
        {renderIconBox(icon)}
        {trailing}
    </View>;

    const renderCaption = (title: string, description: string) => <View
        style={stylesheet.column}
    >
        <Text
            variant="titleSmallSize"
            color="high"
        >
            {title}
        </Text>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {description}
        </Text>
    </View>;

    const renderThermostat = () => <Card
        variant="outline"
    >
        <Card.Content>
            <View
                style={[
                    isWide ? undefined : stylesheet.column,
                    {
                        flexDirection: isWide ? "row" : "column",
                        gap: spaces.spacingLg,
                        alignItems: "center"
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.gauge,
                        {
                            maxWidth: shapeConfigs.sideListWidth + spaces.spacingXxl
                        }
                    ]}
                >
                    <RadialChart
                        valueFormatter={(value) => `${decimal.format(value + MIN_TEMPERATURE)}°`}
                        centerTitle={t("blocks.smartHome01.gaugeNow", [
                            formatTemperature(target)
                        ])}
                        summary={t("blocks.smartHome01.gaugeSummary", [
                            decimal.format(CURRENT_TEMPERATURE),
                            decimal.format(target)
                        ])}
                        value={CURRENT_TEMPERATURE - MIN_TEMPERATURE}
                        maxValue={MAX_TEMPERATURE - MIN_TEMPERATURE}
                        isAnimatedOnMount={true}
                        plotSize="medium"
                        variant="gauge"
                        valueKey="now"
                    />
                </View>
                <View
                    style={[
                        stylesheet.grow,
                        stylesheet.column,
                        {
                            width: isWide ? undefined : "100%",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.titleRow}
                    >
                        <Text
                            variant="titleLargeSize"
                            color="high"
                        >
                            {t("blocks.smartHome01.thermostat")}
                        </Text>
                        <Badge
                            icon={mode === "cool" ? SnowflakeIcon : FlameIcon}
                            title={t(`blocks.smartHome01.state_${mode}`)}
                            type={mode === "cool" ? "info" : "warning"}
                            spreadBehaviour="free"
                            variant="tonal"
                        />
                    </View>
                    <View
                        style={[
                            stylesheet.target,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            accessibilityLabel={t("blocks.smartHome01.lower")}
                            onPress={handleTarget(-TEMPERATURE_STEP)}
                            isDisabled={target <= SLIDER_MIN}
                            spreadBehaviour="free"
                            variant="outline"
                            icon={MinusIcon}
                            type="neutral"
                        />
                        <View
                            style={stylesheet.center}
                        >
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.smartHome01.target")}
                            </Text>
                            <Text
                                variant="headlineMediumSize"
                                color="high"
                            >
                                {formatTemperature(target)}
                            </Text>
                        </View>
                        <Button
                            accessibilityLabel={t("blocks.smartHome01.raise")}
                            onPress={handleTarget(TEMPERATURE_STEP)}
                            isDisabled={target >= SLIDER_MAX}
                            spreadBehaviour="free"
                            variant="outline"
                            icon={PlusIcon}
                            type="neutral"
                        />
                    </View>
                    <Slider
                        accessibilityLabel={t("blocks.smartHome01.target")}
                        formatValueText={formatTemperature}
                        onValueChange={handleTargetSlide}
                        isShowMinMaxLabels={true}
                        step={TEMPERATURE_STEP}
                        max={SLIDER_MAX}
                        min={SLIDER_MIN}
                        value={target}
                    />
                    <ToggleGroup
                        title={t("blocks.smartHome01.mode")}
                        onValueChange={handleModeChange}
                        attachBehaviour="attached"
                        isClearEnabled={false}
                        variant="outline"
                        value={mode}
                    >
                        {
                            MODES.map((item) => <ToggleGroup.Item
                                title={t(`blocks.smartHome01.mode_${item.id}`)}
                                icon={item.icon}
                                value={item.id}
                                key={item.id}
                            />)
                        }
                    </ToggleGroup>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.smartHome01.conditions", [
                            percent.format(0.46),
                            formatTemperature(OUTSIDE_TEMPERATURE),
                            formatTemperature(20)
                        ])}
                    </Text>
                </View>
            </View>
        </Card.Content>
    </Card>;

    const renderLights = () => renderTile(
        <>
            {
                renderTop(Lightbulb, <Switch
                    accessibilityLabel={t("blocks.smartHome01.lights")}
                    onCheckedChange={setIsLightOn}
                    isChecked={isLightOn}
                />)
            }
            {
                renderCaption(
                    t("blocks.smartHome01.lights"),
                    isLightOn ? t("blocks.smartHome01.lightsOn", [
                        percent.format(brightness / 100)
                    ]) : t("blocks.smartHome01.lightsOff")
                )
            }
            <Slider
                accessibilityLabel={t("blocks.smartHome01.brightness")}
                onValueChange={handleBrightness}
                isDisabled={!isLightOn}
                value={brightness}
                max={100}
                min={0}
            />
        </>,
        "lights"
    );

    const renderCurtains = () => renderTile(
        <>
            {
                renderTop(Blinds, <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {percent.format(curtains / 100)}
                </Text>)
            }
            {renderCaption(t("blocks.smartHome01.curtains"), t("blocks.smartHome01.curtainsHint"))}
            <Slider
                accessibilityLabel={t("blocks.smartHome01.curtains")}
                onValueChange={handleCurtains}
                value={curtains}
                max={100}
                min={0}
            />
        </>,
        "curtains"
    );

    const renderClimate = () => renderTile(
        <>
            {
                renderTop(Wind, <Switch
                    accessibilityLabel={t("blocks.smartHome01.climate")}
                    onCheckedChange={setIsClimateOn}
                    isChecked={isClimateOn}
                />)
            }
            {
                renderCaption(
                    t("blocks.smartHome01.climate"),
                    isClimateOn ? t("blocks.smartHome01.climateOn") : t("blocks.smartHome01.climateOff")
                )
            }
            <View
                style={[
                    stylesheet.badges,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Badge
                    title={t("blocks.smartHome01.dry")}
                    variant="outline"
                    type="neutral"
                />
                <Badge
                    title={t("blocks.smartHome01.quiet")}
                    variant="outline"
                    type="neutral"
                />
            </View>
        </>,
        "climate"
    );

    const renderVacuum = () => renderTile(
        <>
            {
                renderTop(Bot, <Badge
                    title={isCleaning ? t("blocks.smartHome01.cleaning") : t("blocks.smartHome01.charging")}
                    icon={isCleaning ? PlayIcon : ChargingIcon}
                    type="neutral"
                />)
            }
            {renderCaption(t("blocks.smartHome01.vacuum"), t("blocks.smartHome01.vacuumHint", [
                percent.format(0.86),
                "09:10"
            ]))}
            <Button
                title={isCleaning ? t("blocks.smartHome01.stopClean") : t("blocks.smartHome01.startClean")}
                onPress={() => setIsCleaning(!isCleaning)}
                style={stylesheet.start}
                variant="outline"
                icon={PlayIcon}
                type="neutral"
                size="small"
            />
        </>,
        "vacuum"
    );

    const renderLock = () => renderTile(
        <>
            {
                renderTop(Lock, <Badge
                    title={isLocked ? t("blocks.smartHome01.locked") : t("blocks.smartHome01.unlocked")}
                    type={isLocked ? "success" : "warning"}
                    icon={CheckIcon}
                />)
            }
            {renderCaption(t("blocks.smartHome01.lock"), t(isLocked ? "blocks.smartHome01.lockHint" : "blocks.smartHome01.lockOpenHint", [
                "08:12"
            ]))}
            <Button
                title={isLocked ? t("blocks.smartHome01.unlock") : t("blocks.smartHome01.lockNow")}
                onPress={() => setIsLocked(!isLocked)}
                style={stylesheet.start}
                variant="outline"
                type="neutral"
                size="small"
            />
        </>,
        "lock"
    );

    const renderAir = () => renderTile(
        <>
            {
                renderTop(Wind, <Badge
                    title={t("blocks.smartHome01.good")}
                    icon={CheckIcon}
                    type="success"
                />)
            }
            {renderCaption(t("blocks.smartHome01.air"), t("blocks.smartHome01.airHint", [
                integer.format(412),
                percent.format(0.46)
            ]))}
            <Progress
                title={t("blocks.smartHome01.air")}
                type="success"
                value={84}
            />
        </>,
        "air"
    );

    return <ScrollView
        contentContainerStyle={[
            stylesheet.screen,
            {
                maxWidth: shapeConfigs.contentMaxWidth,
                padding: spaces.spacingMd,
                gap: spaces.spacingLg
            }
        ]}
    >
        <View
            style={[
                stylesheet.header,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.grow}
            >
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {dateText}
                </Text>
                <Text
                    variant="headlineMediumSize"
                    color="high"
                >
                    {t("blocks.smartHome01.title")}
                </Text>
            </View>
            <Card
                variant="outline"
            >
                <Card.Content>
                    <View
                        style={[
                            stylesheet.weather,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Icon
                            icon={CloudSun}
                            color="warning"
                        />
                        <View>
                            <Text
                                variant="titleLargeSize"
                                color="high"
                            >
                                {`${integer.format(OUTSIDE_TEMPERATURE)} °C`}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.smartHome01.weather")}
                            </Text>
                        </View>
                    </View>
                </Card.Content>
            </Card>
            <Button
                accessibilityLabel={t("blocks.smartHome01.settings")}
                spreadBehaviour="free"
                onPress={handlePress}
                icon={SlidersIcon}
                variant="outline"
                type="neutral"
            />
        </View>
        <Tabs
            onValueChange={setRoom}
            variant="line"
            value={room}
        >
            <Tabs.List
                title={t("blocks.smartHome01.rooms")}
            >
                {
                    ROOMS.map((item) => <Tabs.Trigger
                        title={t(`blocks.smartHome01.room_${item.id}`)}
                        count={item.count}
                        value={item.id}
                        key={item.id}
                    />)
                }
            </Tabs.List>
        </Tabs>
        <View
            style={[
                stylesheet.scenes,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="labelMediumSize"
                color="mid"
            >
                {t("blocks.smartHome01.scenes")}
            </Text>
            {
                SCENES.map((item) => <Chip
                    title={t(`blocks.smartHome01.scene_${item.id}`)}
                    isSelected={scene === item.id}
                    onPress={handleScene(item.id)}
                    spreadBehaviour="free"
                    icon={item.icon}
                    type="neutral"
                    key={item.id}
                />)
            }
            <Button
                title={t("blocks.smartHome01.newScene")}
                spreadBehaviour="free"
                onPress={handlePress}
                variant="ghost"
                icon={PlusIcon}
                type="neutral"
                size="small"
            />
        </View>
        {renderThermostat()}
        <View
            style={[
                stylesheet.tiles,
                {
                    margin: -spaces.spacingMd / 2
                }
            ]}
        >
            {renderLights()}
            {renderCurtains()}
            {renderClimate()}
            {renderVacuum()}
            {renderLock()}
            {renderAir()}
        </View>
        <Chart
            footer={<View
                style={[
                    stylesheet.energyFooter,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Text
                    variant="headlineSmallSize"
                    color="high"
                >
                    {formatKwh(totalKwh)}
                </Text>
                <Badge
                    title={t("blocks.smartHome01.energyDelta", [
                        percent.format(0.12)
                    ])}
                    icon={TrendingDownIcon}
                    spreadBehaviour="free"
                    type="success"
                />
            </View>}
            description={t("blocks.smartHome01.energyRange")}
            title={t("blocks.smartHome01.energyTitle")}
            spreadBehaviour="stretch"
            isAnimatedOnMount={true}
            config={energyConfig}
        >
            <AreaChart
                categoryTitle={t("blocks.smartHome01.hour")}
                valueFormatter={formatKwh}
                categoryKey="hour"
                data={energyRows}
                plotSize="medium"
                seriesKeys={[
                    "kwh"
                ]}
            />
        </Chart>
    </ScrollView>;
};
export default SmartHome01;
